import * as classNames from 'classnames';
import * as React from 'react';
import withStyles, { StyledComponentProps } from 'react-jss';
import { Link } from 'react-router-dom';
import { InjectedApiProps, withApi } from 'src/_context/Api';
import { InjectedAppScrollerProps, withAppScroller } from 'src/_context/AppScroller';
import responsive from 'src/constants/responsive';
import spacing from 'src/constants/spacing';
import stylesheet, { directions } from 'src/constants/stylesheet';
import { Language } from 'src/utils/Languages';
import Part from './Part';

export type IntroProps =
    React.HTMLAttributes<HTMLElement> &
    {
        text: string;
        articles: ArticleDto[];
        loading: boolean;
    };

const styles = {
    container: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column'
    },
    content: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column'
    },
    main: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'row-reverse',
        height: '100%',

        [responsive.Mobile]: {
            flexDirection: 'column'
        },
        [responsive.AfterMobile]: {
            '&:before': {
                content: '""',
                position: 'absolute',
                left: '50%',
                width: 1,
                background: 'black',
                ...stylesheet.PostionGutter(1, directions.top | directions.bottom)
            }
        },
    },
    scrollLink: {
        flex: 0,
        display: 'flex',
        alignItems: 'center',
        textAlign: 'center',
        boxSizing: 'border-box',
        justifyContent: 'center',
        position: 'relative',
        ...stylesheet.Caption,
        [responsive.Mobile]: {
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 20,
            paddingRight: 20
        },
        [responsive.Tablet]: {
            padding: spacing.Gutter.Mobile
        },
        [responsive.AfterTablet]: {
            padding: spacing.Gutter.Desktop
        },
        '&:after': {
            content: '""',
            position: 'absolute',
            ...stylesheet.MarginGutter(1, directions.sides),
            height: 1,
            left: 0,
            right: 0,
            top: 0,
            background: 'black'
        }
    },
    generalIntroduction: {
        display: 'flex',
        flex: '1 0 auto',
        alignItems: 'center',
        textAlign: 'center',
        boxSizing: 'border-box',
        ...stylesheet.PaddingGutter(1, directions.all),
        [responsive.Mobile]: {},
        [responsive.AfterMobile]: {
            height: '100%',
            display: 'flex',
            width: '50%',
            flex: '1 0 50%',
            justifyContent: 'center',
        }
    },
    Subtitle_2: {
        ...stylesheet.Subtitle_2,
    },
    generalIntroductionSpan: {
        [responsive.AfterMobile]: {
            ...stylesheet.PaddingGutter(.5, directions.sides)
        }
    },
    menus: {
        position: 'relative',

        [responsive.Mobile]: {},
        [responsive.AfterMobile]: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '50%',
            flex: '1 0 50%',
            ...stylesheet.PaddingGutter(1, directions.all),
            '&:before': {
                content: '""',
                position: 'absolute',
                top: '50%',
                height: 1,
                background: 'black',
                ...stylesheet.PostionGutter(1, directions.sides),
            }
        },
    },
    menu: {
        height: '50%',
        display: 'flex',
        alignItems: 'center',
        textAlign: 'center',
        boxSizing: 'border-box',
        justifyContent: 'center',
        [responsive.Mobile]: {
            position: 'relative',
            '&:before': {
                content: '""',
                position: 'absolute',
                top: 0,
                height: 1,
                background: 'black',
                ...stylesheet.PostionGutter(1, directions.sides),
            }
        },
        [responsive.AfterMobile]: {
            ...stylesheet.PaddingGutter(.5, directions.sides)
        }
    },
    menuLink: {
        ...stylesheet.Link(false, 'center')
    },
    submenuLink: {
        ...stylesheet.Link(false, 'center', 5)
    },
    menuTitle: {
        ...stylesheet.Subtitle_2,
        marginBottom: 6,
        [responsive.Mobile]: {
            paddingTop: 10,
            paddingBottom: 4,
            paddingLeft: 20,
            paddingRight: 20
        }
    },
    menuItem: {
        ...stylesheet.Subtitle_1,
        marginBottom: 4,
        [responsive.Mobile]: {
            display: 'none'
        }
    },

    cookieSection: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        boxSizing: 'border-box',
        ...stylesheet.Caption,
        background: 'white',
        zIndex: 99999,
        [responsive.Mobile]: {
            flex: 0,
            flexDirection: 'column',
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 20,
            paddingRight: 20
        },
        [responsive.AfterMobile]: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
        },
        [responsive.Tablet]: {
            padding: spacing.Gutter.Mobile
        },
        [responsive.AfterTablet]: {
            padding: spacing.Gutter.Desktop
        },
        '&:after': {
            content: '""',
            position: 'absolute',
            ...stylesheet.MarginGutter(1, directions.sides),
            height: 1,
            left: 0,
            right: 0,
            top: 0,
            background: 'black'
        }
    },
    separator: {
        display: 'inline-block',
        margin: '0 .3rem',

        [responsive.Mobile]: {
            display: 'none',
        },
    },
    closeCookie: {
        [responsive.Mobile]: {
            display: 'inline-block',
            marginTop: 5
        },
    }
};

type Props =
    IntroProps &
    InjectedAppScrollerProps &
    InjectedApiProps &
    StyledComponentProps<keyof typeof styles>;

type State = {
};
class Intro extends React.Component<Props, State> {
    public readonly state: State = {
    };
    private cookieElt: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
    constructor(props: Props) {
        super(props);
        this._handleCloseCookie = this._handleCloseCookie.bind(this);
    }

    render() {
        const { children, classes, loading, articles, text } = this.props;
        const scrollTopText = Language === 'en' ? 'Scroll to read more' : 'En savoir plus';

        return (
            <Part
                containerProps={{ className: classes!.container }}
                contentProps={{ className: classes!.content }}
                loading={loading}
                url={`/${Language}/home/intro`}
            >
                <div className={classes!.main}>
                    <div className={classes!.generalIntroduction}>
                        <span
                            className={classNames(classes!.generalIntroductionSpan, classes!.Subtitle_2)}
                            dangerouslySetInnerHTML={{ __html: text }} />
                    </div>
                    <div className={classes!.menus}>
                        {articles.map((a, i) => {
                            const sections = a.Sections;
                            return (
                                <nav className={classes!.menu} key={a.UID}>
                                    <ul>
                                        <li
                                            className={classNames(classes!.menuTitle, classes!.Subtitle_2)}
                                        >
                                            <Link
                                                className={classes!.menuLink}
                                                to={`/${Language}/home/${a.UID}`}
                                            >
                                                {a.Title}
                                            </Link>
                                        </li>
                                        {sections.map((s, j) => {
                                            return (
                                                <li
                                                    key={s.UID}
                                                    className={classes!.menuItem}
                                                >
                                                    <Link
                                                        className={classes!.submenuLink}
                                                        to={`/${Language}/home/${a.UID}/${s.UID}`}
                                                    >
                                                        {s.Title}
                                                    </Link>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </nav>
                            );
                        })}
                    </div>
                </div>
                {children}
                <Link to={`/${Language}/home/${articles[0].UID}`} className={classes!.scrollLink}>
                    <span>{scrollTopText}</span>
                </Link>
            </Part>);
    }

    private _handleCloseCookie(e: React.MouseEvent<HTMLAnchorElement>) {
        e.preventDefault();
        e.stopPropagation();

        if (this.cookieElt.current) {
            this.cookieElt.current.style.display = 'none';
        }
    }
}

export default withStyles(styles)(withAppScroller(withApi(Intro)));


// WEBPACK FOOTER //
// ./src/components/Intro.tsx